import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { elementToPdfBase64 } from "@/lib/pdfSend";
import { ISSUER, BANK, LEGAL_NOTE, lineCalc, calcTotals, fmtDateIt } from "@/lib/invoice";
import { ArrowLeft, Download, MessageCircle, Printer } from "lucide-react";

const num = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });
const fmt = (n) => `${num(n)} €`;
const sheetEl = () => document.querySelector('[data-testid="fattura-sheet"]');

export default function Fattura() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => setError("Fattura non trovata"));
  }, [id]);

  if (error) return <div className="p-10 text-center text-slate-500">{error}</div>;
  if (!inv) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;
  const fname = `FAT_${inv.number.replace(/\//g, "")}.pdf`;

  const download = async () => {
    setBusy(true);
    try {
      const b64 = await elementToPdfBase64(sheetEl());
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${b64}`;
      a.download = fname;
      a.click();
    } catch (e) { toast.error("Generazione PDF non riuscita"); }
    setBusy(false);
  };

  const whatsapp = async () => {
    const phone = inv.client?.phone || window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
    if (!phone) return;
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const pdf_base64 = await elementToPdfBase64(sheetEl());
      const { data } = await api.post(`/invoices/${inv.id}/document`, { pdf_base64, public_base: API.replace(/\/api\/?$/, "") });
      const msg = `Gentile ${inv.client?.name || "cliente"}, di seguito la fattura ${inv.number}${inv.guest_name ? ` (${inv.guest_name})` : ""} di Blu Lodge Maldives:\n${data.public_url}`;
      const url = `https://wa.me/${phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(msg)}`;
      if (win) win.location.href = url; else window.location.href = url;
      toast.success("Apertura WhatsApp…");
    } catch (e) { if (win) win.close(); toast.error("Invio non riuscito"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } .sheet { box-shadow: none !important; } }`}</style>
      <div className="max-w-3xl mx-auto mb-4 flex flex-wrap gap-2 justify-between no-print">
        <button data-testid="fattura-back" onClick={() => navigate("/fatture")} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft size={16} /> Fatture</button>
        <div className="flex gap-2">
          <button data-testid="fattura-download" onClick={download} disabled={busy} className="flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50"><Download size={16} /> Scarica PDF</button>
          <button data-testid="fattura-whatsapp" onClick={whatsapp} disabled={busy} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50"><MessageCircle size={16} /> WhatsApp</button>
          <button data-testid="fattura-print" onClick={() => window.print()} className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md"><Printer size={16} /> Stampa</button>
        </div>
      </div>
      <InvoiceSheet inv={inv} />
    </div>
  );
}

function InvoiceSheet({ inv }) {
  const t = calcTotals(inv.lines);
  const c = inv.client || {};
  return (
    <div className="sheet max-w-3xl mx-auto bg-white p-10 shadow-sm text-slate-800" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="fattura-sheet">
      <div className="flex justify-between items-start">
        <img src="/blu-lodge-logo.png" alt="Blu Lodge Maldives" className="w-28 h-28 object-contain" data-testid="fattura-logo" />
        <div className="text-right">
          <div className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>FATTURA</div>
          <div className="text-sm mt-2"><span className="text-slate-500">NUMERO:</span> <b data-testid="fattura-number">{inv.number}</b></div>
          <div className="text-sm"><span className="text-slate-500">DATA DOCUMENTO:</span> <b>{fmtDateIt(inv.date)}</b></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8 mt-8 text-xs leading-relaxed">
        <div>
          <div className="font-bold uppercase tracking-wide text-slate-500 border-b border-slate-300 pb-1 mb-2">Nostre informazioni</div>
          <div className="font-bold text-sm">{ISSUER.name}</div>
          <div>{ISSUER.address}</div><div>P.IVA: {ISSUER.vat}</div>
          <div>{ISSUER.email}</div><div>{ISSUER.phone}</div><div>{ISSUER.web}</div>
        </div>
        <div data-testid="fattura-client">
          <div className="font-bold uppercase tracking-wide text-slate-500 border-b border-slate-300 pb-1 mb-2">Informazioni cliente</div>
          <div className="font-bold text-sm">{c.name}</div>
          {c.address && <div>{c.address}</div>}
          {c.vat && <div>P.IVA/TAX ID: {c.vat}</div>}
          {c.cf && <div>C.F.: {c.cf}</div>}
          {c.sdi && <div>SDI {c.sdi}</div>}
        </div>
      </div>
      <table className="w-full text-xs mt-8">
        <thead>
          <tr className="bg-slate-800 text-white text-left">
            <th className="px-2 py-2">DESCRIZIONE</th><th className="px-2 py-2 text-right">Q.TÀ</th><th className="px-2 py-2 text-right">UNIT.</th>
            <th className="px-2 py-2 text-right">IMP.</th><th className="px-2 py-2 text-right">SCONTO</th><th className="px-2 py-2 text-right">IVA</th><th className="px-2 py-2 text-right">IMPORTO</th>
          </tr>
        </thead>
        <tbody>
          {inv.lines.map((l, i) => { const k = lineCalc(l); return (
            <tr key={i} className="border-b border-slate-200">
              <td className="px-2 py-2">{l.description}</td><td className="px-2 py-2 text-right">{l.qty}</td>
              <td className="px-2 py-2 text-right tabular">{fmt(l.unit)}</td><td className="px-2 py-2 text-right tabular">{fmt(k.imp)}</td>
              <td className="px-2 py-2 text-right tabular">{fmt(l.discount)}</td><td className="px-2 py-2 text-right tabular">{fmt(k.iva)}</td>
              <td className="px-2 py-2 text-right tabular font-semibold">{fmt(k.importo)}</td>
            </tr>
          ); })}
        </tbody>
      </table>
      <div className="grid grid-cols-3 gap-6 mt-6 text-xs">
        <table className="col-span-2 w-full">
          <thead><tr className="border-b border-slate-300 text-left text-slate-500"><th className="py-1">Aliquota IVA</th><th className="py-1 text-right">Imponibile</th><th className="py-1 text-right">Imposta</th></tr></thead>
          <tbody><tr><td className="py-1 pr-2">{inv.vat_note}</td><td className="py-1 pl-2 text-right tabular whitespace-nowrap">{fmt(t.imponibile)}</td><td className="py-1 pl-2 text-right tabular whitespace-nowrap">{fmt(t.imposta)}</td></tr></tbody>
        </table>
        <div className="bg-slate-100 rounded-md p-3 flex justify-between items-center self-start">
          <span className="font-bold text-sm">Totale</span><span className="font-extrabold text-lg tabular" data-testid="fattura-total">{fmt(t.total)}</span>
        </div>
      </div>
      <div className="mt-8 text-xs leading-relaxed">
        <div className="font-bold uppercase tracking-wide text-slate-500 border-b border-slate-300 pb-1 mb-2">Pagamento</div>
        <div>{inv.payment_method}: <b>{num(t.total)} EUR</b></div>
        <div className="mt-2 font-semibold">Dati per bonifico:</div>
        <div>Istituto Finanziario: {BANK.bank}</div><div>IBAN: {BANK.iban}</div><div>BIC/SWIFT: {BANK.bic}</div>
        {inv.notes && <div className="mt-3 text-slate-600">{inv.notes}</div>}
      </div>
      <div className="mt-10 pt-3 border-t border-slate-200 text-[10px] text-slate-500 flex justify-between gap-4">
        <span>{LEGAL_NOTE}</span><span className="shrink-0">Pagina 1 di 1</span>
      </div>
    </div>
  );
}
